import { Box, Checkbox, FormHelperText, FormLabel } from "@mui/joy";
import { useController, useFormContext } from "react-hook-form";

import {
  TraitName,
  TraitValue,
  getTraitChoices,
  getTraitIcon,
  getTraitLabel,
} from "../../utils/traits";
import FormGroup from "./FormGroup";
import MultipleValuesSelectedIndicator from "./MultipleValuesSelected";

type Props<T extends TraitName = TraitName> = {
  traitType: T;
  selectedTraits: TraitValue<T>[];
  setSelectedTraits: (traits: TraitValue<T>[]) => void;
};

export default function TraitSelector<T extends TraitName = TraitName>({
  traitType,
  selectedTraits,
  setSelectedTraits,
}: Props<T>) {
  const choices = getTraitChoices(traitType);

  return (
    <Box
      sx={{
        gap: 1,
        display: "grid",
        // gridTemplateColumns: "repeat(4, 1fr)",
        gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))",
      }}
    >
      {choices.map((choice) => {
        const TraitIcon = getTraitIcon(traitType, choice);
        return (
          <Box
            key={choice}
            sx={{
              position: "relative",
              maxWidth: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              paddingX: 0.25,
              paddingY: 0.75,
              gap: 0.5,
            }}
          >
            <Box
              sx={{
                zIndex: 3,
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                pointerEvents: "none",
              }}
            >
              <TraitIcon size="lg" />
            </Box>
            <Checkbox
              disableIcon
              overlay
              label={getTraitLabel(traitType, choice)}
              color="neutral"
              variant="outlined"
              checked={selectedTraits.includes(choice)}
              onChange={() => {
                setSelectedTraits(
                  selectedTraits.includes(choice)
                    ? selectedTraits.filter((t) => t !== choice)
                    : [...selectedTraits, choice],
                );
              }}
              slotProps={{
                root: {
                  sx: {
                    width: "100%",
                  },
                },
                action: ({ checked }) => ({
                  sx: {
                    // border: "1px solid",
                    // borderColor: "neutral.200",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    borderRadius: 4,
                    ...(checked
                      ? {
                          // border: "1px solid",
                          backgroundColor: "neutral.200",
                          borderColor: "neutral.600",
                          "&:hover": {
                            backgroundColor: "neutral.300",
                          },
                        }
                      : {}),
                  },
                }),
                label: ({ checked }) => ({
                  sx: {
                    fontSize: 12,
                    fontWeight: "bold",
                    textAlign: "center",
                    lineHeight: 1,
                    overflow: "hidden",
                    maxWidth: "100%",
                    textOverflow: "ellipsis",
                    color: checked ? "neutral.800" : "neutral.700",
                  },
                }),
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
}

const emptyArray: never[] = [];

export const TraitSelectorFormInput = <T extends TraitName = TraitName>({
  traitType,
  name,
  label,
}: {
  traitType: T;
  name: string;
  label: string;
}) => {
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    name,
    control,
  });
  return (
    <FormGroup error={Boolean(fieldState.error)}>
      <FormLabel>{label}</FormLabel>
      <MultipleValuesSelectedIndicator name={name} />
      <TraitSelector
        traitType={traitType}
        selectedTraits={field.value || emptyArray}
        setSelectedTraits={(v) => field.onChange(v)}
      />
      <FormHelperText>{fieldState.error?.message}</FormHelperText>
    </FormGroup>
  );
};
